
/*!
 * @pendo/components 3.25.3
 * (c) 2018-present
 */

import 'lodash/isEqual';
import 'just-kebab-case';
import './labels-mixin.js';
import './pendo-divider.js';
import 'lodash/isNumber';
import { r as raf, s as setStyles } from './table-auto-height-mixin.js';
import debounce from 'lodash/debounce';
import PendoMultiselectOption from './pendo-multiselect-option.js';
import computeScrollIntoView from 'compute-scroll-into-view';

let uid = 0;
var script = {
  components: {
    PendoMultiselectOption
  },
  props: {
    activate: {
      type: Function
    },
    allSelectedText: {
      type: String
    },
    computedMenuWidth: {
      type: Number
    },
    selectedLabel: {
      type: [String, Number]
    },
    deactivate: {
      type: Function
    },
    disabled: {
      type: Boolean
    },
    filteredOptions: {
      type: Array
    },
    flattenedOptions: {
      type: Array
    },
    formatOptionLabel: {
      type: Function
    },
    getOptionLabel: {
      type: Function
    },
    groupDividers: {
      type: Boolean
    },
    groupLabelKey: {
      type: String
    },
    groupOptionsKey: {
      type: String
    },
    groupSelect: {
      type: Boolean
    },
    handleKeydown: {
      type: Function
    },
    hasGroups: {
      type: Boolean
    },
    hideSelectedOptions: {
      type: Boolean
    },
    id: {
      type: [String, Number]
    },
    inputValue: {
      type: String
    },
    allOptionsSelected: {
      type: Boolean
    },
    isEntireGroupSelected: {
      type: Function
    },
    isFullWidth: {
      type: Boolean
    },
    isOpen: {
      type: Boolean
    },
    isOptionSelected: {
      type: Function
    },
    isPlaceholderVisible: {
      type: Boolean
    },
    isSelectedLabelVisible: {
      type: Boolean
    },
    labelKey: {
      type: String
    },
    limit: {
      type: Number
    },
    limitText: {
      type: Function
    },
    loading: {
      type: Boolean
    },
    max: {
      type: [Number, Boolean]
    },
    maxMenuHeight: {
      type: [String, Number]
    },
    maxMenuWidth: {
      type: [String, Number]
    },
    minMenuWidth: {
      type: [String, Number]
    },
    model: {
      type: Array
    },
    multiple: {
      type: Boolean
    },
    name: {
      type: String
    },
    noResultsText: {
      type: String
    },
    options: {
      type: null
    },
    optionHeight: {
      type: Number
    },
    placeholder: {
      type: [String, Number]
    },
    pointer: {
      type: Number
    },
    pointerSet: {
      type: Function
    },
    removeSelection: {
      type: Function
    },
    select: {
      type: Function
    },
    selectGroup: {
      type: Function
    },
    selectedValue: {
      type: [Object, Number, String, Array]
    },
    shouldScrollToFocusedOption: {
      type: Boolean
    },
    showNoResults: {
      type: Boolean
    },
    showMaxElements: {
      type: Boolean
    },
    showPointer: {
      type: Boolean
    },
    toggleMenu: {
      type: Function
    },
    updateInputValue: {
      type: Function
    },
    valueKey: {
      type: String
    },
    visibleSelectedValues: {
      type: Array
    }
  },

  data() {
    return {
      listOptions: [],
      scrollTop: 0,
      scrollHeight: 0,
      scrollActive: false,
      buffer: 300,
      scheduleOptionsUpdate: null
    };
  },

  computed: {
    listWidth() {
      if (this.showStateContainer && this.$el) {
        return `${this.$el.clientWidth}px`;
      }

      return '100%';
    },

    listData() {
      return this.hasGroups ? this.flattenedOptions : this.filteredOptions;
    },

    showNoDataSlot() {
      return !Array.isArray(this.options) || !this.options.length;
    },

    showNoResultsSlot() {
      if (this.showNoDataSlot) {
        return false;
      }

      if (this.showAllSelectedSlot) {
        return false;
      }

      if (this.showMaxElementsSlot) {
        return false;
      }

      if (this.showNoResults) {
        const hasInputValue = this.inputValue.length;
        const noMatchingOptions = this.listData.length === 0;
        return Boolean(hasInputValue && noMatchingOptions);
      }

      return false;
    },

    showAllSelectedSlot() {
      if (this.multiple && this.hideSelectedOptions) {
        return this.allOptionsSelected;
      }

      return false;
    },

    showMaxElementsSlot() {
      if (this.showMaxElements && this.multiple && this.max) {
        return this.model.length === this.max;
      }

      return false;
    },

    showStateContainer() {
      if (this.loading) {
        return false;
      }

      return this.showMaxElementsSlot || this.showAllSelectedSlot || this.showNoResultsSlot || this.showNoDataSlot;
    },

    showOptionsList() {
      if (this.showStateContainer) {
        return false;
      }

      return true;
    },

    optionHeights() {
      const listData = this.listData;
      const sizes = {
        '-1': 0
      };
      let total = 0;
      let current;
      let groupIndex = 0;

      for (let i = 0, l = listData.length; i < l; i++) {
        if (listData[i].type === 'group') {
          if (groupIndex === 0) {
            groupIndex++;
            current = this.optionHeight;
          } else {
            current = this.optionHeight + 16;
          }
        } else if (listData[i].divided) {
          current = this.optionHeight + 16;
        } else {
          current = this.optionHeight;
        }

        total += current;
        sizes[i] = total;
      }

      return sizes;
    }

  },
  watch: {
    pointer(index) {
      if (!this.shouldScrollToFocusedOption) {
        return;
      }

      if (this.hasGroups && index < 2) {
        this.$el.scrollTop = 0;
      } else {
        this.scrollHighlightedOptionIntoView(index);
      }
    },

    minMenuWidth: 'setMenuWidth',
    computedMenuWidth: 'setMenuWidth',
    maxMenuWidth: 'setMenuWidth',
    maxMenuHeight: 'setMenuHeight',

    listData() {
      this.updateVisibleOptions();
    }

  },

  created() {
    this.updateScrollStatus = debounce(this.updateScrollStatus, 125, {
      leading: true,
      trailing: true
    });
  },

  async mounted() {
    await this.$nextTick();
    this.setMenuHeight();
    this.setMenuWidth();
    this.scheduleOptionsUpdate = raf(this.updateVisibleOptions);
    this.updateVisibleOptions();
    this.ready = true;
  },

  methods: {
    setMenuWidth() {
      const styles = {};

      if (this.isFullWidth) {
        styles.minWidth = '100%';
        styles.maxWidth = '100%';
      } else {
        styles.minWidth = this.computedMenuWidth || this.minMenuWidth;
        styles.maxWidth = this.maxMenuWidth - 2;
      }

      setStyles(this.$el, styles);
    },

    setMenuHeight() {
      const header = this.$el.previousElementSibling;
      const footer = this.$el.nextElementSibling;
      let maxHeight = this.maxMenuHeight;

      if (header && header.className === 'pendo-multiselect__header') {
        maxHeight -= header.clientHeight;
      }

      if (footer && footer.className === 'pendo-multiselect__footer') {
        maxHeight -= footer.clientHeight;
      }

      setStyles(this.$el, {
        maxHeight
      });
    },

    scrollHighlightedOptionIntoView(index) {
      const elem = this.$el.querySelector(`[data-uid='${index}']`);

      if (!elem) {
        this.$el.scrollTop = this.optionHeights[index - 1];
        return;
      }

      const actions = computeScrollIntoView(elem, {
        scrollMode: 'if-needed',
        block: 'nearest',
        inline: 'nearest',
        boundary: this.$el
      });
      actions.forEach(({
        top
      }) => {
        if (top > this.$el.scrollTop) {
          this.$el.scrollTop = top + 8;
        } else if (this.$el.scrollTop > top) {
          this.$el.scrollTop = top - 8;
        }
      });
    },

    updateScrollStatus() {
      this.scrollActive = false;
    },

    handleScroll(event) {
      const _event$target = event.target,
            clientHeight = _event$target.clientHeight,
            scrollTop = _event$target.scrollTop;

      if (this.scrollTop !== scrollTop) {
        this.scheduleOptionsUpdate({
          start: scrollTop,
          end: scrollTop + clientHeight
        });
        this.scrollActive = true;
        this.updateScrollStatus();
      }
    },

    async updateVisibleOptions(scroll) {
      let startIndex = 0;
      let endIndex = 0;
      let option;
      let data;
      const listData = this.listData,
            listOptions = this.listOptions,
            optionHeight = this.optionHeight,
            buffer = this.buffer,
            hasGroups = this.hasGroups,
            optionHeights = this.optionHeights;
      const count = listData.length;

      if (!scroll) {
        const _this$$el = this.$el,
              scrollTop = _this$$el.scrollTop,
              clientHeight = _this$$el.clientHeight;
        scroll = {
          start: scrollTop,
          end: scrollTop + clientHeight
        };
      }

      const beforeListHeight = this.$refs.beforeList ? this.$refs.beforeList.offsetHeight : 0;
      scroll.start -= beforeListHeight;

      if (count) {
        scroll.start -= buffer;
        scroll.end += buffer;

        if (hasGroups) {
          let scrollStart;
          let a = 0;
          let b = count - 1;
          let currIndex = Math.floor(count / 2);
          let lastIndex;

          do {
            lastIndex = currIndex;
            scrollStart = optionHeights[currIndex];

            if (scrollStart < scroll.start) {
              a = currIndex;
            } else if (currIndex < count - 1 && optionHeights[currIndex + 1] > scroll.start) {
              b = currIndex;
            }

            currIndex = Math.floor((a + b) / 2);
          } while (currIndex !== lastIndex);

          startIndex = Math.max(currIndex, 0);
          this.scrollHeight = optionHeights[count - 1];
          this.scrollTop = Math.floor(optionHeights[startIndex - 1]);

          for (endIndex = currIndex; endIndex < count && optionHeights[endIndex] < scroll.end; endIndex++) {
            if (endIndex === -1) {
              endIndex = count - 1;
            } else {
              endIndex++;
              endIndex = Math.min(endIndex, count);
            }
          }
        } else {
          endIndex = Math.min(Math.ceil(scroll.end / optionHeight), count);
          startIndex = Math.max(Math.floor(scroll.start / optionHeight), 0);
          this.scrollHeight = count * optionHeight;
          this.scrollTop = Math.floor(startIndex * optionHeight);
        }
      } else {
        this.scrollTop = 0;
        this.scrollHeight = 0;
      }

      for (let o = 0, i = startIndex; i < endIndex; o++, i++) {
        data = listData[i];

        if (listOptions[o]) {
          option = listOptions[o];
          option.nr.index = i;
          option.data = data;
        } else {
          option = {
            data
          };
          const nonReactive = {
            id: uid++,
            index: i
          };
          Object.defineProperty(option, 'nr', {
            configurable: false,
            value: nonReactive
          });
          listOptions.push(option);
        }
      }

      if (listOptions.length + startIndex > count) {
        listOptions.length = Math.max(count - startIndex, 0);
      }
    }

  }
};

const __vue_script__ = script;

var __vue_render__ = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', {
    ref: "list",
    staticClass: "pendo-multiselect__content-wrapper",
    attrs: {
      "id": "pendo-multiselect-list",
      "tabindex": "0",
      "role": "listbox",
      "data-optioncount": _vm.listData.length
    },
    on: {
      "&scroll": function ($event) {
        return _vm.handleScroll($event);
      },
      "mouseleave": function ($event) {
        return _vm.pointerSet(-1);
      }
    }
  }, [_c('div', {
    ref: "beforeList",
    staticClass: "pendo-multiselect__before-list"
  }, [_vm._t("_beforeList", null, null, _vm.$props)], 2), _vm._v(" "), _vm.showStateContainer ? _c('div', {
    staticClass: "pendo-multiselect__element",
    style: {
      width: _vm.listWidth
    }
  }, [_vm.showMaxElementsSlot ? _c('span', {
    staticClass: "pendo-multiselect__max-elements"
  }, [_vm._t("_maxElements", [_vm._v("\n                Maximum of " + _vm._s(_vm.max) + " options selected. First remove a selected option to select another.\n            ")])], 2) : _vm._e(), _vm._v(" "), _vm.showAllSelectedSlot ? _c('span', {
    staticClass: "pendo-multiselect__all-selected"
  }, [_vm._t("_allSelected", [_vm._v("\n                " + _vm._s(_vm.allSelectedText) + "\n            ")])], 2) : _vm._e(), _vm._v(" "), _vm.showNoResultsSlot ? _c('span', {
    staticClass: "pendo-multiselect__no-results"
  }, [_vm._t("_noResult", [_vm._v("\n                " + _vm._s(_vm.noResultsText) + "\n            ")])], 2) : _vm._e(), _vm._v(" "), _vm.showNoDataSlot ? _c('span', {
    staticClass: "pendo-multiselect__no-data"
  }, [_vm._t("_noData")], 2) : _vm._e()]) : _vm._e(), _vm._v(" "), _vm.showOptionsList ? [_c('div', {
    style: {
      width: '0px',
      float: 'left',
      height: _vm.scrollHeight + "px"
    }
  }), _vm._v(" "), _c('div', {
    staticClass: "pendo-multiselect__scroll-container",
    style: {
      transform: "translate3d(0px, " + _vm.scrollTop + "px, 0px)"
    }
  }, [_vm._l(_vm.listOptions, function (option) {
    return [_c('pendo-multiselect-option', {
      key: option.nr.id,
      attrs: {
        "index": option.nr.index,
        "scroll-active": _vm.scrollActive,
        "option": option.data
      },
      scopedSlots: _vm._u([{
        key: "optionGroup",
        fn: function () {
          return [_vm._t("_optionGroup", null, {
            "option": option.data,
            "inputValue": _vm.inputValue
          })];
        },
        proxy: true
      }, {
        key: "option",
        fn: function () {
          return [_vm._t("_option", null, {
            "option": option.data,
            "index": option.nr.index
          })];
        },
        proxy: true
      }], null, true)
    })];
  })], 2)] : _vm._e(), _vm._v(" "), _vm._t("_afterList", null, null, _vm.$props)], 2);
};

var __vue_staticRenderFns__ = [];
const __vue_inject_styles__ = undefined;
const __vue_scope_id__ = undefined;
const __vue_is_functional_template__ = false;

function __vue_normalize__(template, style, script, scope, functional, moduleIdentifier, createInjector, createInjectorSSR) {
  const component = (typeof script === 'function' ? script.options : script) || {};
  component.__file = "pendo-multiselect-options.vue";

  if (!component.render) {
    component.render = template.render;
    component.staticRenderFns = template.staticRenderFns;
    component._compiled = true;
    if (functional) component.functional = true;
  }

  component._scopeId = scope;

  return component;
}

var PendoMultiselectOptions = __vue_normalize__({
  render: __vue_render__,
  staticRenderFns: __vue_staticRenderFns__
}, __vue_inject_styles__, __vue_script__, __vue_scope_id__, __vue_is_functional_template__);

export default PendoMultiselectOptions;
